import { MagnifyingGlass } from '@creditornot/cb-icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Input } from '@creditornot/cb-input';
import { Empty } from '@creditornot/cb-placeholders';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { DiscountedItem } from 'modules/campaigns/hooks/useDiscountedItems';

import { CategoryAccordionItem } from './CategoryAccordionItem';
import { DiscountedItemRow } from './DiscountedItemRow';
import {
  DiscountedItemCategoryWithMatches,
  handleCategorySelectionChange,
  handleCategoryVsAllItemsChange,
  handleItemSelectionChange,
} from './utils';

type DiscountedItemsModalContentProps = {
  discountedItems: DiscountedItem[];
  discountedItemCategories: DiscountedItemCategoryWithMatches[];
  selectedItemIds: string[];
  selectedCategoryIds: string[];
  onSelectionChange: (values: { itemIds: string[]; categoryIds: string[] }) => void;
  onFilterChange: (searchTerm: string, additionalShownCategoryIds: string[]) => void;
};

const Container = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

const SearchInputContainer = styled.div`
  padding: 16px;
  border-block-end: 1px solid ${color.border};
`;

const StyledEmpty = styled(Empty)`
  margin-block: 24px;
  flex-grow: 1;
`;

const isCategoryCheckedRecursive = (
  category: DiscountedItemCategoryWithMatches,
  selectedCategoryIds: string[],
  selectedItemIds: string[],
): { isCategorySelected: boolean; checked: boolean; partiallyChecked: boolean } => {
  const isCategorySelected = selectedCategoryIds.includes(category.id);

  const childCategories = category.allChildCategories ?? category.childCategories;

  const childCategoryResults = childCategories.map(childCategory =>
    isCategoryCheckedRecursive(childCategory, selectedCategoryIds, selectedItemIds),
  );

  const itemIds = category.allItemIds ?? category.itemIds;

  const checked =
    isCategorySelected ||
    (childCategoryResults.every(childResult => childResult.checked) &&
      itemIds.every(itemId => selectedItemIds.includes(itemId)));

  const partiallyChecked =
    !checked &&
    (childCategoryResults.some(childResult => childResult.checked || childResult.partiallyChecked) ||
      itemIds.some(itemId => selectedItemIds.includes(itemId)));

  return { isCategorySelected, checked, partiallyChecked };
};

// Helper to handle recursion from child categories
const CategoryContent = ({
  category,
  discountedItems,
  selectedCategoryIds,
  selectedItemIds,
  selectedParentCategory,
  onSelectionChange,
  identationLevel,
  accordionsExpanded,
  additionalShownCategoryIds,
  searchText,
}: {
  category: DiscountedItemCategoryWithMatches;
  discountedItems: DiscountedItem[];
  selectedItemIds: string[];
  selectedCategoryIds: string[];
  selectedParentCategory: DiscountedItemCategoryWithMatches | undefined;
  onSelectionChange: (values: { itemIds: string[]; categoryIds: string[] }, isAddCategory?: boolean) => void;
  identationLevel: number;
  accordionsExpanded: boolean;
  additionalShownCategoryIds: string[];
  searchText: string;
}) => {
  const { isCategorySelected, checked, partiallyChecked } = isCategoryCheckedRecursive(
    category,
    selectedCategoryIds,
    selectedItemIds,
  );
  const isParentSelected = !!selectedParentCategory;

  const isCategoryExpanded = accordionsExpanded || additionalShownCategoryIds.includes(category.id);

  return (
    <CategoryAccordionItem
      id={`category-${category.id}`}
      categoryName={category.name}
      checked={isParentSelected || checked}
      partiallyChecked={!isParentSelected && partiallyChecked}
      onCheckboxClick={newSelectedValue =>
        onSelectionChange(
          handleCategorySelectionChange(
            selectedItemIds,
            selectedCategoryIds,
            category,
            selectedParentCategory,
            newSelectedValue,
          ),
          newSelectedValue,
        )
      }
      includeNewItems={isParentSelected || isCategorySelected}
      onIncludeNewItemsClick={newIncludeValue =>
        onSelectionChange(
          handleCategoryVsAllItemsChange(
            selectedItemIds,
            selectedCategoryIds,
            category,
            selectedParentCategory,
            newIncludeValue,
          ),
        )
      }
      expanded={isCategoryExpanded}
      identationLevel={identationLevel}
    >
      {category.itemIds.map(itemId => {
        const item = discountedItems.find(item => item.id === itemId);
        if (!item) {
          return null;
        }
        return (
          <DiscountedItemRow
            key={itemId}
            item={item}
            selected={isParentSelected || isCategorySelected || selectedItemIds.includes(item.id)}
            onSelect={newSelectedValue =>
              onSelectionChange(
                handleItemSelectionChange(
                  selectedItemIds,
                  selectedCategoryIds,
                  item.id,
                  isCategorySelected ? category : selectedParentCategory,
                  newSelectedValue,
                ),
              )
            }
            identationLevel={identationLevel}
            searchText={searchText}
          />
        );
      })}
      {category.childCategories.map(childCategory => (
        <CategoryContent
          key={`child-${childCategory.id}`}
          category={childCategory}
          discountedItems={discountedItems}
          selectedCategoryIds={selectedCategoryIds}
          selectedItemIds={selectedItemIds}
          selectedParentCategory={isCategorySelected ? category : selectedParentCategory}
          onSelectionChange={onSelectionChange}
          identationLevel={identationLevel + 1}
          accordionsExpanded={isCategoryExpanded}
          additionalShownCategoryIds={additionalShownCategoryIds}
          searchText={searchText}
        />
      ))}
    </CategoryAccordionItem>
  );
};

// If there are a lot of items in the menu, showing them all initially causes performance issues
const EXPAND_ITEM_COUNT_LIMIT = 200;

export const DiscountedItemsModalContent = ({
  discountedItems,
  discountedItemCategories,
  selectedItemIds,
  selectedCategoryIds,
  onSelectionChange,
  onFilterChange,
}: DiscountedItemsModalContentProps) => {
  const { formatMessage } = useIntl();

  const [searchValue, setSearchValue] = useState('');
  // Show the items if there aren't many or if there's a search
  const accordionsExpanded = discountedItems.length < EXPAND_ITEM_COUNT_LIMIT || searchValue.length >= 3;
  const searchTerm = accordionsExpanded ? searchValue : '';

  const [additionalShownCategoryIds, setAdditionalShownCategoryIds] = useState<string[]>([]);

  const onFilteredCategoriesChange = useCallback(
    (searchStr: string, additionalCategoryIds: string[]) => {
      setAdditionalShownCategoryIds(additionalCategoryIds);
      onFilterChange(searchStr, additionalCategoryIds);
    },
    [onFilterChange],
  );

  const onSearchChange = useCallback(
    (str: string) => {
      setSearchValue(str);
      onFilteredCategoriesChange(str, []);
    },
    [onFilteredCategoriesChange],
  );

  return (
    <Container>
      <SearchInputContainer>
        <Input
          icon={<MagnifyingGlass />}
          onClearClick={() => onSearchChange('')}
          onChange={e => onSearchChange(e.target.value)}
          value={searchValue}
          placeholder={formatMessage({ id: 'campaigns.form.item.search-for-item' })}
        />
      </SearchInputContainer>
      {discountedItemCategories.map(category => (
        <CategoryContent
          key={`category-${category.id}`}
          category={category}
          discountedItems={discountedItems}
          selectedCategoryIds={selectedCategoryIds}
          selectedItemIds={selectedItemIds}
          selectedParentCategory={undefined}
          onSelectionChange={(newSelection, isAddCategory) => {
            // Make sure all newly selected items are visible in case there is filtering
            if (isAddCategory && !!searchTerm) {
              const showCategoryIds = newSelection.categoryIds.filter(id => !selectedCategoryIds.includes(id));
              setAdditionalShownCategoryIds(showCategoryIds);
              onFilteredCategoriesChange(searchTerm, showCategoryIds);
            }
            onSelectionChange(newSelection);
          }}
          identationLevel={0}
          accordionsExpanded={accordionsExpanded}
          additionalShownCategoryIds={additionalShownCategoryIds}
          searchText={searchTerm}
        />
      ))}

      {!discountedItemCategories.length && <StyledEmpty title={formatMessage({ id: 'campaigns.form.item.no-item' })} />}
    </Container>
  );
};
